/**
 * Per-sub-nav access guard for Subscriptions. Admins (app admin or subscriptions
 * admin role) see everything; viewers see a sub-nav only if granted
 * (deny-by-default). The Access page (`admin: true`) requires APP admin.
 * `requiresAnalytics` (e.g. the Audio tab) ALSO requires the separate
 * `analytics` claim (`tools.analytics.enabled`) — its data comes from the
 * analytics-service backend, which checks that claim regardless of Subscriptions
 * role/grants, so even an admin without it would just see the tab error.
 */
import type { ReactNode } from 'react';
import { isSubscriptionAdmin, canViewSubscriptionPage } from '@daaily/shared';
import { useProfile } from '../profile/ProfileProvider';

export function SubscriptionPageGuard({ entryKey, admin, requiresAnalytics, children }: {
  entryKey: string; admin?: boolean; requiresAnalytics?: boolean; children: ReactNode;
}) {
  const { state } = useProfile();
  if (state.status !== 'ready') return null;
  const subAdmin = isSubscriptionAdmin({ isAppAdmin: state.isAdmin, role: state.user.tools?.subscriptions?.role });
  const hasAnalytics = state.user.tools?.analytics?.enabled === true;
  // Access page = app-admin only; other pages = admin (all) or granted viewer.
  const allowed = (admin ? state.isAdmin : canViewSubscriptionPage(subAdmin, state.user.subscriptionPages ?? [], entryKey))
    && (!requiresAnalytics || hasAnalytics);
  if (allowed) return <>{children}</>;
  return (
    <main className="w-full max-w-form px-4 md:px-8 pt-6 pb-10">
      <div className="rounded-lg border border-line bg-surface px-6 py-10 text-center">
        <div className="text-sm font-medium text-fg">This page isn't provisioned for you</div>
        <p className="text-sm text-fg-faint mt-1.5">Ask an admin to grant you access to this Subscriptions page.</p>
      </div>
    </main>
  );
}
